import { Text } from "@certn/cascadia";
import {
  ButtonGroup,
  BasicButton,
  PrimaryButton,
  Modal,
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  Link,
} from "@certn/cascadia-react";
import { Dispatch, SetStateAction } from "react";
import { Trans } from "react-i18next";

import type { DbsVariant } from "./DBSConsent";
import { useHandleChange } from "../../utils";

const SKIP_CONSENT_FIELDS: Record<DbsVariant, string[]> = {
  basic: [
    "dbs_consent.has_authorized_certn_to_submit_application",
    "dbs_consent.has_read_privacy_policy",
    "dbs_consent.has_consented_to_sharing_result",
    "dbs_consent.has_provided_complete_and_true_information",
  ],
  enhanced: [
    "enhanced_dbs_consent.enhanced_has_read_privacy_policy",
    "enhanced_dbs_consent.enhanced_has_provided_complete_and_true_information",
    "enhanced_dbs_consent.enhanced_has_understood_role_eligibility",
    "enhanced_dbs_consent.enhanced_has_consented_to_sharing_result",
    "enhanced_dbs_consent.enhanced_has_authorized_certn_to_submit_application",
  ],
  standard: [
    "standard_dbs_consent.standard_has_read_privacy_policy",
    "standard_dbs_consent.standard_has_provided_complete_and_true_information",
    "standard_dbs_consent.standard_has_understood_role_eligibility",
    "standard_dbs_consent.standard_has_consented_to_sharing_result",
    "standard_dbs_consent.standard_has_authorized_certn_to_submit_application",
  ],
};

export function SkipDBSConsentModal({
  setOnSkipConsent,
  variant = "basic",
}: {
  setOnSkipConsent: Dispatch<SetStateAction<boolean>>;
  variant?: DbsVariant;
}) {
  const handleChange = useHandleChange();

  return (
    <Modal testID="skip-dbs-consent-modal">
      <Dialog>
        {({ close }) => (
          <>
            <DialogHeader
              onClose={close}
              testID="skip-dbs-consent-modal-header"
            >
              <Trans>Skip DBS Consent</Trans>
            </DialogHeader>
            <DialogContent testID="skip-dbs-consent-modal-content">
              <Text style="global/body-primary">
                <Trans>
                  If you do not give consent, Certn will not be able to submit
                  your DBS application. In which case you can obtain a DBS check
                  directly via
                </Trans>{" "}
                <Link
                  testID="dbs-link"
                  linkStyle="monochrome"
                  textStyle="global/body-secondary"
                  textColor="text-weak"
                  href="https://www.gov.uk/DBS"
                  target="_blank"
                >
                  www.gov.uk/DBS
                </Link>
                .{" "}
              </Text>
              <Text style="global/body-primary">
                <Trans>Are you sure you want to opt out?</Trans>
              </Text>
            </DialogContent>
            <DialogFooter testID="skip-dbs-consent-modal-footer">
              <ButtonGroup>
                <BasicButton
                  size="medium"
                  onPress={() => {
                    close();
                  }}
                  testID="skip-dbs-consent-cancel-button"
                >
                  <Trans>Cancel</Trans>
                </BasicButton>
                <PrimaryButton
                  testID="skip-dbs-consent-continue-button"
                  size="medium"
                  onPress={() => {
                    for (const field of SKIP_CONSENT_FIELDS[variant]) {
                      handleChange(field, false);
                    }
                    setOnSkipConsent(true);
                    close();
                  }}
                >
                  <Trans>Continue</Trans>
                </PrimaryButton>
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </Modal>
  );
}
